import { useState, useEffect } from 'react'
import { Share } from '@capacitor/share'
import { Capacitor } from '@capacitor/core'
import { getDailyRating } from '../utils/storage'
import Filmstrip from './Filmstrip'
import BottomNav from './BottomNav'
import { IconCheck, IconChart, IconMessage, IconHome, IconCalendar } from './icons'

const RATING_LABELS = { easy: '🟢 Easy', medium: '🟡 Medium', hard: '🔴 Hard' }
const RATING_EMOJIS = { easy: '🟢', medium: '🟡', hard: '🔴' }

function getMsUntilMidnight() {
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(0, 0, 0, 0)
  return tomorrow - now
}

function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return `${h}h ${m.toString().padStart(2, '0')}m ${s.toString().padStart(2, '0')}s`
}

function formatTime(secs) {
  if (secs == null) return null
  const m = Math.floor(secs / 60).toString().padStart(2, '0')
  const s = (secs % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

export default function DailyChallenge({ status, dailyNumber, dailyDate, completedData, onHome, onViewStats }) {
  const dailyRatingKey = dailyDate ? getDailyRating(dailyDate) : null
  const ratingLabel = dailyRatingKey ? RATING_LABELS[dailyRatingKey] ?? null : null
  const ratingEmoji = dailyRatingKey ? RATING_EMOJIS[dailyRatingKey] ?? null : null
  const [countdown, setCountdown] = useState(() => formatCountdown(getMsUntilMidnight()))
  const [copyToast, setCopyToast] = useState(false)

  useEffect(() => {
    if (status !== 'completed') return
    const id = setInterval(() => setCountdown(formatCountdown(getMsUntilMidnight())), 1000)
    return () => clearInterval(id)
  }, [status])

  const displayTime = completedData?.timeSeconds != null
    ? formatTime(completedData.timeSeconds)
    : completedData?.time

  const buildShareText = () => {
    const cueCardsCount = completedData?.hintsUsed ?? 0
    const ratingPart = ratingEmoji ? ` • ${ratingEmoji}` : ''
    const parts = [
      `Action! Daily #${dailyNumber} 🎬`,
      `${completedData?.moves} hops • ${displayTime}${ratingPart}`,
    ]
    if (cueCardsCount > 0) parts.push(`🃏×${cueCardsCount}`)
    parts.push(completedData?.emojiChain ?? '')
    return parts.join('\n')
  }

  // Last-resort clipboard for browsers without navigator.clipboard (older Safari,
  // insecure contexts). Only the web/desktop path uses it — native iOS goes
  // through Capacitor's Share plugin.
  const fallbackCopy = (text) => {
    try {
      const el = document.createElement('textarea')
      el.value = text
      el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0'
      document.body.appendChild(el)
      el.focus(); el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
    } catch {}
  }

  // Wordle-style share: native OS share sheet on iOS/Android (Capacitor),
  // silent clipboard copy + toast on web/desktop. No custom modal.
  const handleShare = async () => {
    const text = buildShareText()
    if (Capacitor.isNativePlatform()) {
      try { await Share.share({ text }) } catch { /* user dismissed — ignore */ }
      return
    }
    const showCopied = () => { setCopyToast(true); setTimeout(() => setCopyToast(false), 2200) }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(showCopied).catch(() => { fallbackCopy(text); showCopied() })
    } else {
      fallbackCopy(text); showCopied()
    }
  }

  return (
    <div className="daily-screen">
      <div className="screen-content">
        {(status === 'loading' || status === 'no-puzzle') && (
          <>
            <div className="daily-screen__body">
              {status === 'loading' && (
                <>
                  <div className="spinner" />
                  <p className="daily-screen__msg" style={{ marginTop: 12 }}>Loading today's puzzle…</p>
                </>
              )}

              {status === 'no-puzzle' && (
                <>
                  <div className="daily-screen__icon"><IconCalendar size={48} /></div>
                  <h2 className="daily-screen__title">No Puzzle Today</h2>
                  <p className="daily-screen__msg">Check back soon — more puzzles are on the way.</p>
                </>
              )}
            </div>

            <BottomNav items={[
              { key: 'home', icon: IconHome, label: 'Home', variant: 'primary', onClick: onHome },
            ]} />
          </>
        )}

        {status === 'completed' && (
          <div className="daily-complete">
            <div className="daily-complete__hero">
              <div className="daily-complete__hero-check"><IconCheck size={36} /></div>
              <h1 className="daily-complete__title">Daily #{dailyNumber}</h1>
              <p className="daily-complete__subtitle">You've already wrapped today's shoot!</p>
            </div>

            {completedData && (
              <div className="run-summary">
                <div className="run-summary__stats">
                  <div className="run-summary__stat">
                    <div className="run-summary__stat-value">{completedData.moves}</div>
                    <div className="run-summary__stat-label">Hops</div>
                  </div>
                  <div className="run-summary__divider" aria-hidden="true" />
                  <div className="run-summary__stat">
                    <div className="run-summary__stat-value">{displayTime ?? '—'}</div>
                    <div className="run-summary__stat-label">Time</div>
                  </div>
                </div>
                {dailyRatingKey && (
                  <div className={`difficulty-pill difficulty-pill--${dailyRatingKey}`}>
                    <span className="difficulty-pill__dot" aria-hidden="true" />
                    {dailyRatingKey}
                  </div>
                )}
              </div>
            )}

            {completedData?.chain && (
              <div className="daily-screen__filmstrip-wrap">
                <Filmstrip chain={completedData.chain} onItemClick={() => {}} hidePlaceholder />
              </div>
            )}

            <div className="daily-actions">
              <div className="daily-action">
                <button
                  className="daily-circle daily-circle--inverse"
                  onClick={handleShare}
                  aria-label="Spoiler Alert"
                  title="Spoiler Alert"
                >
                  <IconMessage size={24} />
                </button>
                <span className="daily-action__label">Spoiler<br />Alert</span>
              </div>
              <div className="daily-action">
                <button
                  className="daily-circle daily-circle--primary"
                  onClick={onHome}
                  aria-label="Home"
                  title="Home"
                >
                  <IconHome size={22} />
                </button>
                <span className="daily-action__label">Home</span>
              </div>
              <div className="daily-action">
                <button
                  className="daily-circle daily-circle--inverse"
                  onClick={onViewStats}
                  aria-label="See Stats"
                  title="See Stats"
                >
                  <IconChart size={24} />
                </button>
                <span className="daily-action__label">See<br />Stats</span>
              </div>
            </div>

            <div className="daily-countdown">
              <div className="daily-countdown__label">Next Puzzle In</div>
              <div className="daily-countdown__time">{countdown}</div>
            </div>

            {copyToast && (
              <div className="copy-toast" role="status">
                <IconCheck size={15} /> Copied to clipboard
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
